import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Flex, Modal, Spin, Tooltip, Typography } from "antd";
import { useForm } from "antd/lib/form/Form";
import _ from "lodash";
import React, { useState } from "react";
import { ContentPartFilter } from "../../../components/content-part/ContentPartFilter";
import { PageHeader } from "../../../components/page-header";
import { EmptyData } from "../../../components/empty/emptyData";
import { useGetDataOnScroll } from "../../../components/getDataOnScrollProps/useGetDataOnScrollProps";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import {
  useLazyGetMovablePropertyListQuery,
  useReportMovablePropertyMutation
} from "../../../redux/api/movableProperty";
import { EntMovableProperty } from "../../../models/movableProperty";
import { EntMovablePropertyFilter } from "../../../models/filters/movablePropertyFilter";
import { useMovableProperty } from "./useMovableProperty";
import { AddMovablePropertyModal } from "./AddMovableProperty";
import { FilterMovableProperty } from "./FilterMovableProperty";
import ReportMovableProperty from "./ReportMovableProperty";

enum FormField {
  conditionIds,
  periodStart,
  periodEnd
}

export const MovableProperty = () => {
  const [modal, contextHolder] = Modal.useModal();
  const [selectedItem, setSelectedItem] = useState<EntMovableProperty>(new EntMovableProperty());
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showReportModal, setShowReportModal] = useState<boolean>(false);
  const [form] = useForm();
  const { t } = useTranslation()

  const [getReportMovableProperty] = useReportMovablePropertyMutation();
  const [getArchiveFolders] = useLazyGetMovablePropertyListQuery();
  const {
	filter,
	fetching,
	data,
	access,
	totalItems,
	setData,
	setFetching,
	setFilter,
	scrollHandler,
	setCurrentPage
  } = useGetDataOnScroll<EntMovableProperty, EntMovablePropertyFilter>({
	initialFilter: new EntMovablePropertyFilter(),
	query: getArchiveFolders
  });

  const { onAddMovableProperty, onDeleteMovableProperty, onEditMovableProperty } = useMovableProperty(modal, () => {
	searchHandleClick();
  })
  const [height, setHeight] = useState<number>(0);

  const storedData = JSON.parse(localStorage.getItem("availableRoutes") || "[]");
  const isReportAvailable = storedData.some((route: any) => (route.alias === "/report" || route.alias === "/interactive-map-ws") && route.methodPost);

  const openAddModal = (item: EntMovableProperty) => {
	setSelectedItem(item);
	setShowModal(true);
  }

  const setEntityFromFields = (): EntMovablePropertyFilter => {
	let filt: EntMovablePropertyFilter = _.clone(filter);
	filt.conditionIds = form.getFieldValue(FormField.conditionIds) ?? null;
	filt.periodStart = form.getFieldValue(FormField.periodStart)?.format("YYYY-MM-DD") ?? null;
	filt.periodEnd = form.getFieldValue(FormField.periodEnd)?.format("YYYY-MM-DD") ?? null;
	filt.page = 1;
	return filt;
  }

  const clearFields = () => {
	form.setFields([
	  { name: FormField.conditionIds, value: null },
	  { name: FormField.periodStart, value: null },
	  { name: FormField.periodEnd, value: null },
	]);
  }

  const searchHandleClick = () => {
	setFilter(setEntityFromFields());
	setData([]);
	setCurrentPage(0);
	setFetching(true);
  }

  const getReport = async (payload: {
	periodStart: string;
	periodEnd: string;
	language: string,
	governmentAlias: string|null
  }): Promise<Blob> => {
	return await getReportMovableProperty(payload).unwrap();
  };

  const openReportModal = () => {
	setShowReportModal(true);
  };

  const table = (
	<div style={ { maxHeight: `${ height }px`, overflow: 'auto' } } onScroll={ scrollHandler }>
	  <table>
		<thead>
		<tr>
		  <th style={ { width: "40px" } }>#</th>
		  <th>{ t('general.name') } </th>
		  <th>{ t('movable_property.brand') }</th>
		  <th>{ t('movable_property.model') }</th>
		  <th>{ t('breeding_farms.quantity') }</th>
		  <th style={ { width: "170px" } }>{ t('mechanisation.transport.description') }</th>
		  <th>{ t('titles.transport_status') }</th>
		  <th>{ t('titles.governments') }</th>
		  <th>{ t('titles.territorial_units') }</th>
		  <th>{ t('mechanisation.total_account.address') }</th>
		  <th style={ { width: "120px" } }></th>
		</tr>
		</thead>
		<tbody>
		{ fetching ? (
		  <tr>
			<th colSpan={ 7 }>
			  <Spin spinning={ fetching } style={ {
				width: '100%',
				height: '180px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center'
			  } }/>
			</th>
		  </tr>
		) : null }
		{ data.length !== 0 ? data.map((item, index) => (
		  <tr key={ item.id }>
			<td>{ ++index }</td>
			<td>{ item.name }</td>
			<td>{ item.brand }</td>
			<td>{ item.model }</td>
			<td>{ item.quantity }</td>
			<td>{ item.description }</td>
			<td>{ i18n.language === 'ru' ? item.condition?.nameRu : item.condition?.nameKy }</td>
			<td>{ i18n.language === 'ru' ? item.government?.nameRu : item.government?.nameKy }</td>
			<td>{ i18n.language === 'ru' ? item.territorialUnit?.nameRu : item.territorialUnit?.nameKy }</td>
			<td>{ item.address }</td>
			<td>
			  <Flex gap="small">
				{ access && access.methodPut && (
				  <Tooltip title={ t('general.edit') }>
					<Button className='btn_edit' onClick={ () => {
					  openAddModal(item)
					} } type="primary" shape="circle" icon={ <EditOutlined/> }/>
				  </Tooltip>
				) }
				{ access && access.methodDelete && (
				  <Tooltip title={ t('general.delete') }>
					<Button className='btn_edit' danger onClick={ () => {
					  onDeleteMovableProperty(item)
					} } type="primary" shape="circle" icon={ <DeleteOutlined/> }/>
				  </Tooltip>
				) }
			  </Flex>
			</td>
		  </tr>
		)) : (
		  <tr>
			<td colSpan={ 9 }>
			  <EmptyData/>
			</td>
		  </tr>
		) }
		</tbody>
	  </table>
	</div>
  );

  return (
	<>
	  <PageHeader
		title={ t('titles.movable_property') }
		additionalInfo={
		  <Typography.Text style={ { marginBottom: '0.5em', display: 'block' } } type="secondary">
			{ t('general.records_found') } { totalItems }
		  </Typography.Text>
		}
	  />
	  { access && access.methodPost && (
		<Button type="primary" icon={ <PlusOutlined/> } onClick={ () => {
		  openAddModal(new EntMovableProperty())
		} }>
		  { t('general.add') }
		</Button>
	  ) }
	  { isReportAvailable && (
		<Button style={ { marginLeft: '5px' } } type="primary" icon={ <PlusOutlined/> }
				onClick={ () => openReportModal() }>
		  { t('general.generate_report') }
		</Button>
	  ) }
	  <ContentPartFilter
		filter={ <FilterMovableProperty
		  onSearch={ searchHandleClick }
		  onReset={ () => {
			clearFields();
			searchHandleClick()
		  } }
		  formField={ FormField }
		  form={ form }
		/> }
		setHeight={ setHeight }
		table={ table }
	  />

	  <AddMovablePropertyModal
		movableProperty={ selectedItem }
		show={ showModal }
		onHide={ () => {
		  setShowModal(false)
		} }
		onSubmit={ selectedItem.id !== 0 ? onEditMovableProperty : onAddMovableProperty }
	  />
	  <ReportMovableProperty
		getReport={ getReport }
		show={ showReportModal }
		onHide={ () => setShowReportModal(false) }
	  />
	  { contextHolder }
	</>
  );
}
